import { Component, useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api, { fmtNZD, API } from "@/lib/api";
import PlanAnalysisBlock from "@/components/PlanAnalysisBlock";
import MerchantImporterModal from "@/components/MerchantImporterModal";
import StageSelect from "@/components/StageSelect";
import NumberInput from "@/components/NumberInput";

const TABS = [
  { key: "resources", label: "Labour Rates" },
  { key: "materials", label: "Materials" },
  { key: "labour", label: "Labour" },
  { key: "misc", label: "Additional Items" },
  { key: "subcontractors", label: "Subcontractors" },
  { key: "sums", label: "Allowances" },
  { key: "overhead", label: "Preliminaries" },
  { key: "total", label: "Summary" },
  { key: "export", label: "Quote Builder" },
];

class EstimateErrorBoundary extends Component {
  constructor(p){ super(p); this.state = { err: null }; }
  static getDerivedStateFromError(err){ return { err }; }
  componentDidCatch(err, info){ console.error("[EstimateTab]", err, info); }
  render(){
    if (this.state.err) return (
      <div className="card" data-testid="estimate-error-boundary">
        <h3 className="text-silver mb-1">Could not render the Estimate</h3>
        <div className="text-sm opacity-70 mb-3">{String(this.state.err?.message||this.state.err)}</div>
        <button className="btn-silver" data-testid="btn-estimate-retry"
                onClick={()=>{ this.setState({err:null}); this.props.onRetry?.(); }}>
          Retry
        </button>
      </div>
    );
    return this.props.children;
  }
}

import AcceptQuote from "./AcceptQuote";

export default function EstimateTab() {
  const { project } = useOutletContext();
  const [est, setEst] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  const [tab, setTab] = useState("resources");
  const [saving, setSaving] = useState(false);
  const [importMerchant, setImportMerchant] = useState("ITM");
  const [importCategory, setImportCategory] = useState("Materials");
  const [stages, setStages] = useState([]);
  const [labourFactors, setLabourFactors] = useState([]);
  const [reprice, setReprice] = useState(null);  // preview payload
  const [repriceUndo, setRepriceUndo] = useState(null); // version_id after apply
  const fileRef = useRef(null);

  async function load() {
    setLoadErr("");
    try {
      const [{ data }, s, lf] = await Promise.all([
        api.get(`/projects/${project.id}/estimate`),
        api.get(`/projects/${project.id}/stages`).catch(() => ({ data: [] })),
        api.get(`/labour-factors`).catch(() => ({ data: [] })),
      ]);
      setEst(data); setStages(s.data || []); setLabourFactors(lf.data || []);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setLoadErr(typeof d === "string" ? d : (d?.message || e?.message || "Could not load estimate"));
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [project.id]);

  // DEBOUNCED save — on each keystroke we update the local `est` state optimistically
  // (so the UI stays responsive) and the server PUT only fires 500ms after Luke stops typing.
  // This fixes the "typing lag" on tables with 134+ lines where every char was a full server round-trip.
  const saveTimer = useRef(null);
  const pendingData = useRef(null);
  async function flushSave() {
    const dataToSave = pendingData.current;
    if (!dataToSave) return;
    pendingData.current = null;
    setSaving(true);
    try {
      const { data } = await api.put(`/projects/${project.id}/estimate`, { data: dataToSave });
      // Only replace .totals so we don't yank the user's current cursor state
      setEst(prev => prev ? { ...prev, totals: data.totals, net_cost: data.net_cost } : data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setLoadErr(typeof d === "string" ? d : (d?.message || e?.message || "Save failed"));
    } finally { setSaving(false); }
  }
  function patchData(fn) {
    // Optimistic local update — instant
    setEst(prev => {
      const copy = { ...prev, data: JSON.parse(JSON.stringify(prev.data)) };
      fn(copy.data);
      pendingData.current = copy.data;
      return copy;
    });
    // Debounce the server save
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(flushSave, 500);
  }
  // Flush any pending save on unmount / tab change so no edits are lost
  useEffect(() => () => {
    if (saveTimer.current) { clearTimeout(saveTimer.current); flushSave(); }
  // eslint-disable-next-line
  }, []);
  async function saveVersion() {
    const name = window.prompt("Version name:");
    if (!name) return;
    try {
      await api.post(`/projects/${project.id}/estimate/version`, { name });
      alert("Version saved");
    } catch (e) {
      alert(e?.response?.data?.detail || "Save failed");
    }
  }
  async function uploadCsv() {
    const f = fileRef.current.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f); fd.append("merchant", importMerchant); fd.append("category", importCategory);
    try {
      const { data } = await api.post(`/projects/${project.id}/estimate/import-csv`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      alert(`${importMerchant} import: ${data.added} added, ${data.unmatched} unmatched`);
      fileRef.current.value = ""; load();
    } catch (e) {
      alert(e?.response?.data?.detail || "CSV import failed");
    }
  }

  if (loadErr) return (
    <div className="card" data-testid="estimate-load-error">
      <h3 className="text-silver mb-1">Could not load the Estimate</h3>
      <div className="text-sm opacity-70 mb-3">{loadErr}</div>
      <button className="btn-silver" onClick={load} data-testid="btn-estimate-reload">Retry</button>
    </div>
  );
  if (!est) return <div className="opacity-60">Loading…</div>;
  const totals = est.totals || {};
  const data = est.data;

  return (
    <div className="space-y-4" data-testid="estimate-tab">
      <div className="flex flex-wrap gap-1 border-b border-neutral-800 pb-2">
        {TABS.map(t => (
          <button key={t.key} data-testid={`etab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`px-3 py-1.5 text-sm rounded ${tab === t.key ? "bg-silver text-black" : "text-white hover:text-silver"}`}>
            {t.label}
          </button>
        ))}
        <div className="ml-auto flex gap-2">
          <button onClick={saveVersion} className="btn-ghost text-sm" data-testid="btn-save-version">Save named version</button>
          <span className="text-sm opacity-60 self-center">{saving ? "Saving…" : "Saved"}</span>
        </div>
      </div>

      <EstimateErrorBoundary onRetry={load}>
        {tab === "resources" && <ResourcesPane data={data} save={patchData} />}
        {tab === "materials" && <>
          <PlanAnalysisBlock projectId={project.id} onAdded={load}/>
          <MaterialsPane data={data} save={patchData} onImport={() => { }} fileRef={fileRef} uploadCsv={uploadCsv} importMerchant={importMerchant} setImportMerchant={setImportMerchant} importCategory={importCategory} setImportCategory={setImportCategory} projectId={project.id} onReload={load} stages={stages} factors={labourFactors}/>
        </>}
        {tab === "labour" && <LabourPane data={data} save={patchData} stages={stages} projectId={project.id} onReload={load}/>}
        {tab === "misc" && <SimplePane data={data} field="misc" save={patchData} label="Additional Items" />}
        {tab === "subcontractors" && <SubsPane data={data} save={patchData} stages={stages} projectId={project.id} onReload={load}/>}
        {tab === "sums" && <SumsPane data={data} save={patchData} />}
        {tab === "overhead" && <SimplePane data={data} field="overhead" save={patchData} label="Preliminaries" />}
        {tab === "total" && <TotalPane project={project} data={data} totals={totals} save={patchData} projectId={project.id}
                                       reprice={reprice} setReprice={setReprice}
                                       repriceUndo={repriceUndo} setRepriceUndo={setRepriceUndo}
                                       onReload={load}/>}
        {tab === "export" && <ExportPane project={project} data={data} totals={totals} />}
      </EstimateErrorBoundary>
    </div>
  );
}

function ResourcesPane({ data, save }) {
  const r = data?.resources || { hours_day: 9, hours_week: 45, flat_rates: [], gang_positions: [] };
  const flatRates = Array.isArray(r.flat_rates) ? r.flat_rates : [];
  return (
    <div className="card">
      <h3 className="text-silver mb-3">Default work hours & flat rates</h3>
      <div className="grid grid-cols-2 gap-3 mb-4">
        <label className="text-sm">Hours/day<NumberInput className="input" value={r.hours_day ?? 9} onChange={e=>save(d=>{ d.resources = d.resources||{}; d.resources.hours_day = parseFloat(e.target.value||0);})}/></label>
        <label className="text-sm">Hours/week<NumberInput className="input" value={r.hours_week ?? 45} onChange={e=>save(d=>{ d.resources = d.resources||{}; d.resources.hours_week = parseFloat(e.target.value||0);})}/></label>
      </div>
      <table className="table"><thead><tr><th>Role</th><th>Charge-out rate ($/hr)</th><th></th></tr></thead>
        <tbody>{flatRates.map((row,i) => (
          <tr key={i}>
            <td><input className="input" value={row?.role || ""} onChange={e=>save(d=>{ d.resources=d.resources||{}; d.resources.flat_rates=d.resources.flat_rates||[]; d.resources.flat_rates[i].role = e.target.value; })}/></td>
            <td><NumberInput className="input" money value={row?.rate || 0} onChange={e=>save(d=>{ d.resources=d.resources||{}; d.resources.flat_rates=d.resources.flat_rates||[]; d.resources.flat_rates[i].rate = parseFloat(e.target.value||0); })}/></td>
            <td><button className="text-xs text-red-400" onClick={() => save(d=>d.resources.flat_rates.splice(i,1))}>Delete</button></td>
          </tr>
        ))}</tbody></table>
      <button className="btn-ghost mt-3 text-sm" onClick={() => save(d=>{ d.resources=d.resources||{}; d.resources.flat_rates=d.resources.flat_rates||[]; d.resources.flat_rates.push({role:"",rate:0}); })}>+ Add role</button>
    </div>
  );
}

function MaterialsPane({ data, save, fileRef, uploadCsv, importMerchant, setImportMerchant, importCategory, setImportCategory, projectId, onReload, stages, factors }) {
  const cats = data.materials;
  const [helpOpen, setHelpOpen] = useState(() => localStorage.getItem("lwco_materials_help_dismissed") !== "1");
  const [importerOpen, setImporterOpen] = useState(false);
  function dismissHelp() { localStorage.setItem("lwco_materials_help_dismissed", "1"); setHelpOpen(false); }
  function reopenHelp() { localStorage.removeItem("lwco_materials_help_dismissed"); setHelpOpen(true); }
  function materialQty(it) { return (parseFloat(it.qty||0)) * (1 + (parseFloat(it.waste_pct||0)/100)) * (parseFloat(it.adj||1)); }

  return (
    <div className="space-y-3" data-testid="materials-pane">
      {helpOpen ? (
        <div className="card border border-silver/40" data-testid="materials-help">
          <div className="flex items-start gap-2">
            <div className="flex-1">
              <div className="text-silver font-bold text-sm mb-1">How this works</div>
              <div className="text-sm opacity-80 leading-relaxed">
                Add items under a trade category. Each row: product, Build Qty, unit, rate.
                Totals work out for you. Fast way: Import a merchant CSV.
              </div>
            </div>
            <button className="btn-ghost text-xs" onClick={dismissHelp} data-testid="btn-dismiss-help" aria-label="Dismiss">Dismiss</button>
          </div>
        </div>
      ) : (
        <button className="text-xs opacity-60 hover:opacity-100" onClick={reopenHelp} data-testid="btn-reopen-help">Show help</button>
      )}

      <div className="card">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
          <h3 className="text-silver">Import merchant quote (CSV)</h3>
          <span className="opacity-60 text-xs">ITM · PlaceMakers · Mitre 10 · Carters · Bunnings · Any</span>
        </div>
        <button onClick={() => setImporterOpen(true)} className="btn-silver" data-testid="btn-open-importer">
          Open importer (preview → map → commit)
        </button>
      </div>
      {importerOpen && (
        <MerchantImporterModal projectId={projectId}
          onClose={() => setImporterOpen(false)}
          onImported={() => { onReload?.(); }}/>
      )}

      {(cats || []).length === 0 && (
        <div className="card opacity-70 text-sm" data-testid="materials-empty-root">
          No categories yet. Add a category below or import a merchant CSV.
        </div>
      )}

      {cats.map((c, ci) => (
        <div key={ci} className="card" data-testid={`materials-cat-${ci}`}>
          <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
            <input className="input max-w-xs" value={c.category} onChange={e=>save(d=>d.materials[ci].category = e.target.value)}/>
            <button className="text-xs text-red-400" onClick={()=>save(d=>d.materials.splice(ci,1))}>Delete category</button>
          </div>
          <div className="overflow-x-auto">
            <table className="table min-w-[720px] md:min-w-full">
              <thead className="sticky top-0 bg-black z-10">
                <tr>
                  <th className="text-left">Product</th>
                  <th className="text-right" title="Build Qty × (1 + Waste %) × Adjustment">Material Qty</th>
                  <th className="text-right">Build Qty</th>
                  <th>Unit</th>
                  <th className="text-right">Rate $</th>
                  <th className="text-right" title="Adjustment multiplier (e.g. 1.1 = +10%). Use for over-order factors.">Adj</th>
                  <th className="text-right" title="Waste percentage added on top of Build Qty. 10 = 10% extra.">Waste %</th>
                  <th className="text-right">Total</th>
                  <th>Stage</th>
                  <th title="hrs/unit × Qty = line labour hours">Labour factor</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {(c.items||[]).map((it, ii) => (
                  <tr key={it.id||ii}>
                    <td><input className="input" value={it.desc||""} onChange={e=>save(d=>d.materials[ci].items[ii].desc=e.target.value)}/></td>
                    <td className="text-right text-silver" data-testid={`mat-material-qty-${ci}-${ii}`}>{materialQty(it).toFixed(2)}</td>
                    <td><NumberInput className="input text-right" value={it.qty||0} onChange={e=>save(d=>d.materials[ci].items[ii].qty=parseFloat(e.target.value||0))}/></td>
                    <td><input className="input" value={it.unit||"ea"} onChange={e=>save(d=>d.materials[ci].items[ii].unit=e.target.value)}/></td>
                    <td><NumberInput className="input text-right" money value={it.rate||0} onChange={e=>save(d=>d.materials[ci].items[ii].rate=parseFloat(e.target.value||0))}/></td>
                    <td><NumberInput className="input text-right" step="0.01" value={it.adj ?? 1} title="Adjustment multiplier (e.g. 1.1 = +10%)" onChange={e=>save(d=>d.materials[ci].items[ii].adj=parseFloat(e.target.value||1))}/></td>
                    <td><NumberInput className="input text-right" value={it.waste_pct||0} title="Waste percentage added on top (e.g. 10 = +10%)" onChange={e=>save(d=>d.materials[ci].items[ii].waste_pct=parseFloat(e.target.value||0))}/></td>
                    <td className="text-right">{fmtNZD(it.total)}</td>
                    <td>
                      <StageSelect projectId={projectId} value={it.stage_id} stages={stages}
                        onStagesChanged={onReload}
                        onChange={(sid)=>save(d=>{
                          d.materials[ci].items[ii].stage_id = sid || null;
                          d.materials[ci].items[ii].stage_name = sid ? (stages.find(s=>s.id===sid)?.name || "Unassigned") : "Unassigned";
                        })}
                        data-testid={`mat-stage-${ci}-${ii}`}/>
                    </td>
                    <td data-testid={`mat-lf-${ci}-${ii}`}>
                      <LabourFactorPicker item={it} factors={factors||[]}
                        onChange={(patch) => save(d => { Object.assign(d.materials[ci].items[ii], patch); })}/>
                    </td>
                    <td><button className="text-xs text-red-400" onClick={()=>save(d=>d.materials[ci].items.splice(ii,1))}>Delete</button></td>
                  </tr>
                ))}
                {(c.items||[]).length === 0 && (
                  <tr><td colSpan="11" className="opacity-50 text-center py-3" data-testid={`materials-empty-${ci}`}>
                    No items yet — add an item or import a CSV.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
          <button className="btn-silver text-sm mt-2" data-testid={`btn-add-material-${ci}`}
            onClick={()=>save(d=>{ if(!d.materials[ci].items) d.materials[ci].items=[]; d.materials[ci].items.push({id:(crypto.randomUUID?.()||String(Date.now())),desc:"",qty:0,unit:"ea",rate:0,waste_pct:0,adj:1}); })}>
            + Add item
          </button>
        </div>
      ))}
      <button className="btn-ghost text-sm" data-testid="btn-add-material-category"
        onClick={()=>save(d=>d.materials.push({category:"New category", items:[]}))}>
        + Add category
      </button>
    </div>
  );
}

function LabourPane({ data, save, stages, projectId, onReload }) {
  return (
    <div className="card">
      <table className="table">
        <thead><tr><th>Task</th><th>Qty</th><th>Factor</th><th>Difficulty</th><th>Hrs</th><th>Rate</th><th>Total</th><th>Stage</th><th></th></tr></thead>
        <tbody>{(data.labour[0]?.items||[]).map((it, i) => (
          <tr key={i}>
            <td><input className="input" value={it.desc||""} onChange={e=>save(d=>{ensureLabCat(d); d.labour[0].items[i].desc=e.target.value})}/></td>
            <td><NumberInput className="input" value={it.qty||0} onChange={e=>save(d=>d.labour[0].items[i].qty=parseFloat(e.target.value||0))}/></td>
            <td><NumberInput className="input" value={it.labour_factor||0} onChange={e=>save(d=>d.labour[0].items[i].labour_factor=parseFloat(e.target.value||0))}/></td>
            <td><select className="input" value={it.difficulty||"Medium"} onChange={e=>save(d=>d.labour[0].items[i].difficulty=e.target.value)}>
              <option>Easy</option><option>Medium</option><option>Hard</option><option>Very Hard</option>
            </select></td>
            <td>{it.man_hours||0}</td>
            <td><NumberInput className="input" money value={it.rate||0} onChange={e=>save(d=>d.labour[0].items[i].rate=parseFloat(e.target.value||0))}/></td>
            <td>{fmtNZD(it.total)}</td>
            <td>
              <StageSelect projectId={projectId} stages={stages} value={it.stage_id}
                onStagesChanged={onReload}
                onChange={(sid)=>save(d=>{
                  d.labour[0].items[i].stage_id = sid || null;
                  d.labour[0].items[i].stage_name = sid ? (stages.find(s=>s.id===sid)?.name || "Unassigned") : "Unassigned";
                })}
                data-testid={`lab-stage-${i}`}/>
            </td>
            <td><button className="text-xs text-red-400" onClick={()=>save(d=>d.labour[0].items.splice(i,1))}>Delete</button></td>
          </tr>
        ))}</tbody>
      </table>
      <button className="btn-ghost text-sm mt-2" onClick={()=>save(d=>{ensureLabCat(d); d.labour[0].items.push({desc:"",qty:0,labour_factor:0,difficulty:"Medium",rate:65})})}>+ Add labour line</button>
    </div>
  );
}
function ensureLabCat(d){ if(!d.labour.length) d.labour.push({category:"Labour", items:[]}); if(!d.labour[0].items) d.labour[0].items = []; }

function SimplePane({ data, field, save, label }) {
  const rows = data[field] || [];
  return (
    <div className="card">
      <h3 className="text-silver mb-2">{label}</h3>
      <table className="table">
        <thead><tr><th>Item</th><th>Qty</th><th>Labour rate</th><th>Material rate</th><th>Total</th><th></th></tr></thead>
        <tbody>{rows.map((it,i)=>(
          <tr key={i}>
            <td><input className="input" value={it.desc||""} onChange={e=>save(d=>d[field][i].desc=e.target.value)}/></td>
            <td><NumberInput className="input" value={it.qty||0} onChange={e=>save(d=>d[field][i].qty=parseFloat(e.target.value||0))}/></td>
            <td><NumberInput className="input" money value={it.labour_rate||0} onChange={e=>save(d=>d[field][i].labour_rate=parseFloat(e.target.value||0))}/></td>
            <td><NumberInput className="input" money value={it.material_rate||0} onChange={e=>save(d=>d[field][i].material_rate=parseFloat(e.target.value||0))}/></td>
            <td>{fmtNZD(it.total)}</td>
            <td><button className="text-xs text-red-400" onClick={()=>save(d=>d[field].splice(i,1))}>Delete</button></td>
          </tr>
        ))}</tbody>
      </table>
      <button className="btn-ghost text-sm mt-2" onClick={()=>save(d=>d[field].push({desc:"",qty:0,labour_rate:0,material_rate:0}))}>+ Add</button>
    </div>
  );
}

function SubsPane({ data, save, stages, projectId, onReload }) {
  const cats = data.subcontractors || [];
  return (
    <div className="space-y-3">
      {cats.map((c, ci) => (
        <div key={ci} className="card">
          <input className="input max-w-xs mb-2" value={c.trade||""} onChange={e=>save(d=>d.subcontractors[ci].trade=e.target.value)}/>
          <table className="table">
            <thead><tr><th>Selected</th><th>Company</th><th>Quote total</th><th>Stage</th><th></th></tr></thead>
            <tbody>{(c.quotes||[]).map((q,qi)=>(
              <tr key={qi}>
                <td><input type="checkbox" checked={!!q.selected} onChange={e=>save(d=>d.subcontractors[ci].quotes[qi].selected=e.target.checked)}/></td>
                <td><input className="input" value={q.company||""} onChange={e=>save(d=>d.subcontractors[ci].quotes[qi].company=e.target.value)}/></td>
                <td><NumberInput className="input" money value={q.total||0} onChange={e=>save(d=>d.subcontractors[ci].quotes[qi].total=parseFloat(e.target.value||0))}/></td>
                <td>
                  <StageSelect projectId={projectId} stages={stages} value={q.stage_id}
                    onStagesChanged={onReload}
                    onChange={(sid)=>save(d=>{
                      d.subcontractors[ci].quotes[qi].stage_id = sid || null;
                      d.subcontractors[ci].quotes[qi].stage_name = sid ? (stages.find(s=>s.id===sid)?.name || "Unassigned") : "Unassigned";
                    })}
                    data-testid={`sub-stage-${ci}-${qi}`}/>
                </td>
                <td><button className="text-xs text-red-400" onClick={()=>save(d=>d.subcontractors[ci].quotes[qi].splice(qi,1))}>Delete</button></td>
              </tr>
            ))}</tbody>
          </table>
          <button className="btn-ghost text-sm mt-2" onClick={()=>save(d=>{if(!d.subcontractors[ci].quotes) d.subcontractors[ci].quotes=[]; d.subcontractors[ci].quotes.push({company:"",total:0,selected:false})})}>+ Add quote</button>
        </div>
      ))}
      <button className="btn-ghost text-sm" onClick={()=>save(d=>d.subcontractors.push({trade:"New trade", quotes:[]}))}>+ Add trade</button>
    </div>
  );
}

function SumsPane({ data, save }) {
  const prov = data.sums?.provisional || []; const net = data.sums?.net || [];
  return (
    <>
      <div className="card">
        <h3 className="text-silver mb-2">Provisional Sums</h3>
        {prov.map((p,i)=>(
          <div key={i} className="flex gap-2 mb-2">
            <input className="input flex-1" value={p.item||""} onChange={e=>save(d=>d.sums.provisional[i].item=e.target.value)}/>
            <NumberInput className="input w-40" money value={p.amount||0} onChange={e=>save(d=>d.sums.provisional[i].amount=parseFloat(e.target.value||0))}/>
            <label className="text-sm flex items-center gap-1"><input type="checkbox" checked={!!p.gst_inc} onChange={e=>save(d=>d.sums.provisional[i].gst_inc=e.target.checked)}/>inc GST</label>
            <button className="text-xs text-red-400" onClick={()=>save(d=>d.sums.provisional.splice(i,1))}>Delete</button>
          </div>
        ))}
        <button className="btn-ghost text-sm" onClick={()=>save(d=>{d.sums=d.sums||{provisional:[],net:[]}; d.sums.provisional.push({item:"",amount:0,gst_inc:false})})}>+ Add provisional sum</button>
      </div>
      <div className="card mt-3">
        <h3 className="text-silver mb-2">Net Sums</h3>
        {net.map((p,i)=>(
          <div key={i} className="flex gap-2 mb-2">
            <input className="input flex-1" value={p.item||""} onChange={e=>save(d=>d.sums.net[i].item=e.target.value)}/>
            <NumberInput className="input w-40" money value={p.amount||0} onChange={e=>save(d=>d.sums.net[i].amount=parseFloat(e.target.value||0))}/>
            <label className="text-sm flex items-center gap-1"><input type="checkbox" checked={!!p.quoted} onChange={e=>save(d=>d.sums.net[i].quoted=e.target.checked)}/>Quoted</label>
            <button className="text-xs text-red-400" onClick={()=>save(d=>d.sums.net.splice(i,1))}>Delete</button>
          </div>
        ))}
        <button className="btn-ghost text-sm" onClick={()=>save(d=>{d.sums=d.sums||{provisional:[],net:[]}; d.sums.net.push({item:"",amount:0,quoted:false})})}>+ Add net sum</button>
      </div>
    </>
  );
}

function TotalPane({ project, data, totals, save, projectId, reprice, setReprice, repriceUndo, setRepriceUndo, onReload }) {
  const m = data.margins || {};
  // Material-driven labour is ALWAYS included in each stage's budget (switch retired).
  const include = true;
  const byStage = totals.build_hours_by_stage || {};
  const byTrade = totals.build_hours_by_trade || {};
  const matByStage = totals.material_labour_hours_by_stage || {};
  const matByTrade = totals.material_labour_hours_by_trade || {};
  const labByStage = totals.labour_hours_by_stage || {};
  const labByTrade = totals.labour_hours_by_trade || {};
  const dblCounted = Object.keys(labByStage).filter(k => matByStage[k]);

  async function doPreview() {
    const { data: pv } = await api.post(`/projects/${projectId}/estimate/reprice-labour-factors/preview`);
    setReprice(pv);
  }
  async function doApply() {
    const { data: res } = await api.post(`/projects/${projectId}/estimate/reprice-labour-factors/apply`);
    setRepriceUndo(res.undo_version_id);
    setReprice(null);
    onReload?.();
  }
  async function doUndo() {
    await api.post(`/projects/${projectId}/estimate/import-undo`, { version_id: repriceUndo });
    setRepriceUndo(null);
    onReload?.();
  }

  return (
    <div className="card space-y-2" data-testid="estimate-summary">
      <Row k="Labour hours (Labour tab)" v={totals.labour_hours}/>
      <Row k="Material-driven hours" v={totals.material_labour_hours_total || 0}/>
      <Row k="Labour $ (Labour tab)" v={fmtNZD(totals.labour_dollars)}/>
      <Row k={`Material-driven labour $ (hrs × $${totals.labour_default_rate || 0}/h default)`} v={fmtNZD(totals.material_labour_dollars_total || 0)}/>
      {include && (
        <Row k="Effective labour $ (Labour tab + material-driven)" v={fmtNZD(totals.effective_labour_dollars_total || 0)}/>
      )}
      <Row k="Materials $" v={fmtNZD(totals.materials_total)}/>
      <Row k="Additional Items $" v={fmtNZD(totals.misc_total)}/>
      <Row k="Subcontractors (selected) $" v={fmtNZD(totals.subcontractors_total)}/>
      <Row k="Allowances $" v={fmtNZD(totals.sums_total)}/>
      <Row k="Preliminaries $" v={fmtNZD(totals.overhead_total)}/>
      <Row k="NET COST" v={fmtNZD(totals.net_cost)} bold/>

      <div className="border border-neutral-800 rounded p-3 mt-3" data-testid="material-labour-rollup">
        <div className="text-sm">
          <b className="text-silver">Material-driven labour</b> is always rolled into each stage's labour budget.
          {dblCounted.length > 0 && (
            <span className="text-amber-300 ml-2 text-xs" data-testid="dbl-count-warn">
              ⚠ You also have Labour-tab lines for: {dblCounted.join(", ")} — both are shown separately below, no double-counting unless you duplicate lines.
            </span>
          )}
        </div>
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <div className="text-silver text-xs mb-1">Total build hours by STAGE</div>
            <table className="table text-xs" data-testid="hours-by-stage">
              <thead><tr><th>Stage</th><th className="text-right">Labour tab</th><th className="text-right">Materials</th><th className="text-right">{include ? "Combined" : "—"}</th></tr></thead>
              <tbody>
                {Object.keys({...labByStage, ...matByStage, ...byStage}).sort().map(k => (
                  <tr key={k}>
                    <td>{k}</td>
                    <td className="text-right">{(labByStage[k]||0).toFixed(2)}</td>
                    <td className="text-right">{(matByStage[k]||0).toFixed(2)}</td>
                    <td className="text-right text-silver">{include ? ((byStage[k]||0).toFixed(2)) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div>
            <div className="text-silver text-xs mb-1">Total build hours by TRADE</div>
            <table className="table text-xs" data-testid="hours-by-trade">
              <thead><tr><th>Trade</th><th className="text-right">Labour tab</th><th className="text-right">Materials</th><th className="text-right">{include ? "Combined" : "—"}</th></tr></thead>
              <tbody>
                {Object.keys({...labByTrade, ...matByTrade, ...byTrade}).sort().map(k => (
                  <tr key={k}>
                    <td>{k}</td>
                    <td className="text-right">{(labByTrade[k]||0).toFixed(2)}</td>
                    <td className="text-right">{(matByTrade[k]||0).toFixed(2)}</td>
                    <td className="text-right text-silver">{include ? ((byTrade[k]||0).toFixed(2)) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="mt-3">
          <div className="text-silver text-xs mb-1">
            Labour $ by STAGE (hours × ${totals.labour_default_rate || 0}/h default from Labour Rates)
          </div>
          <table className="table text-xs" data-testid="labour-dollars-by-stage">
            <thead><tr><th>Stage</th><th className="text-right">Labour-tab $</th><th className="text-right">Material-driven $</th><th className="text-right">{include ? "Effective $" : "—"}</th></tr></thead>
            <tbody>
              {Object.keys({...(totals.labour_dollars_by_stage||{}), ...(totals.material_labour_dollars_by_stage||{}), ...(totals.effective_labour_dollars_by_stage||{})}).sort().map(k => (
                <tr key={k}>
                  <td>{k}</td>
                  <td className="text-right">{fmtNZD((totals.labour_dollars_by_stage||{})[k])}</td>
                  <td className="text-right">{fmtNZD((totals.material_labour_dollars_by_stage||{})[k])}</td>
                  <td className="text-right text-silver">{include ? fmtNZD((totals.effective_labour_dollars_by_stage||{})[k]) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="text-xs text-silver mt-2">Total build hours: <b data-testid="total-build-hours">{(totals.total_build_hours||0).toFixed(2)}</b></div>
      </div>

      <div className="border border-neutral-800 rounded p-3 mt-3" data-testid="reprice-panel">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="text-silver text-sm">Reprice labour factors</div>
          <div className="flex gap-2">
            {!reprice && !repriceUndo && (
              <button className="btn-ghost text-xs" onClick={doPreview} data-testid="btn-reprice-preview">Preview new vs old</button>
            )}
            {reprice && (
              <>
                <button className="btn-ghost text-xs" onClick={()=>setReprice(null)}>Cancel</button>
                <button className="btn-silver text-xs" onClick={doApply} data-testid="btn-reprice-apply">Apply ({reprice.lines_changed} lines)</button>
              </>
            )}
            {repriceUndo && (
              <button className="btn-ghost text-xs text-red-400" onClick={doUndo} data-testid="btn-reprice-undo">Undo reprice</button>
            )}
          </div>
        </div>
        {reprice && (
          <div className="mt-2" data-testid="reprice-preview">
            <table className="table text-xs">
              <thead><tr><th>Line</th><th className="text-right">Old hrs</th><th className="text-right">New hrs</th><th>Trade</th></tr></thead>
              <tbody>
                {reprice.rows.slice(0, 20).map((r,i) => (
                  <tr key={i}>
                    <td className="opacity-80 max-w-[320px] truncate">{r.desc}</td>
                    <td className="text-right">{r.old_hours.toFixed(2)}</td>
                    <td className="text-right text-silver">{r.new_hours.toFixed(2)}</td>
                    <td className="text-xs opacity-70">{r.old_trade || "—"} → {r.new_trade || "—"}</td>
                  </tr>
                ))}
                {reprice.rows.length > 20 && <tr><td colSpan="4" className="opacity-50">…and {reprice.rows.length-20} more lines</td></tr>}
              </tbody>
            </table>
            <div className="mt-2 text-xs opacity-80">
              Per-stage delta: {reprice.stage_diff.map(s=> `${s.stage}: ${s.delta>=0?"+":""}${s.delta.toFixed(2)}h`).join(" · ") || "no change"}
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-3 gap-2 mt-4">
        <label className="text-sm">Labour margin %<NumberInput className="input" value={m.labour||0} onChange={e=>save(d=>d.margins.labour=parseFloat(e.target.value||0))}/></label>
        <label className="text-sm">Materials margin %<NumberInput className="input" value={m.materials||0} onChange={e=>save(d=>d.margins.materials=parseFloat(e.target.value||0))}/></label>
        <label className="text-sm">Subs margin %<NumberInput className="input" value={m.subcontractors||0} onChange={e=>save(d=>d.margins.subcontractors=parseFloat(e.target.value||0))}/></label>
      </div>
      <Row k="Tender Profit" v={fmtNZD(totals.tender_profit)}/>
      <Row k="Total including profit" v={fmtNZD(totals.total_including_profit)}/>
      <AcceptQuote project={project} subtotal={totals.total_including_profit || 0}/>
      <Row k="GST (15%)" v={fmtNZD(totals.gst)}/>
      <div className="pt-3 border-t border-neutral-800">
        <div className="text-silver text-2xl font-bold" data-testid="total-inc-gst">TOTAL INCLUDING GST: {fmtNZD(totals.total_including_gst)}</div>
      </div>
    </div>
  );
}

/**
 * LabourFactorPicker — per-line Labour factor dropdown with auto/manual badge.
 * Local state so typing hrs/unit never writes on every keystroke.
 */
function LabourFactorPicker({ item, factors, onChange }) {
  const source = item.labour_factor_source || "auto";
  const hpu = item.labour_factor_hours ?? "";
  const trade = item.labour_factor_trade || "";
  const [localHpu, setLocalHpu] = useState(String(hpu ?? ""));
  useEffect(() => { setLocalHpu(String(hpu ?? "")); }, [hpu]);
  function pick(id) {
    if (!id) { onChange({ labour_factor_matched_id: null, labour_factor_source: null, labour_factor_hours: 0, labour_factor_trade: null }); return; }
    const f = factors.find(x => x.id === id);
    if (!f) return;
    onChange({ labour_factor_matched_id: id, labour_factor_source: "manual", labour_factor_hours: f.hours_per_unit, labour_factor_trade: f.trade });
  }
  function hpuBlur() {
    const v = parseFloat(localHpu || 0);
    onChange({ labour_factor_source: "manual", labour_factor_hours: v });
  }
  const hoursOnLine = ((parseFloat(item.qty || 0) || 0) * (parseFloat(hpu || 0) || 0)).toFixed(2);
  return (
    <div className="flex items-center gap-1 min-w-[220px]">
      <select className="input text-xs" value={item.labour_factor_matched_id || ""} onChange={e=>pick(e.target.value)}>
        <option value="">— pick trade/keyword —</option>
        {factors.map(f => <option key={f.id} value={f.id}>{f.trade} · {f.keyword.split("|")[0]} · {f.hours_per_unit}h/{f.unit}</option>)}
      </select>
      <NumberInput className="input text-xs text-right w-16" step="0.01" placeholder="hrs/u"
             value={localHpu} onChange={e=>setLocalHpu(e.target.value)} onBlur={hpuBlur}/>
      <span className={`text-[9px] px-1 rounded ${source==="manual" ? "bg-amber-500 text-black" : "bg-silver text-black"}`}
            data-testid="lf-badge">
        {source || "—"}
      </span>
      <span className="text-[10px] opacity-70">{hoursOnLine}h {trade?`· ${trade}`:""}</span>
    </div>
  );
}
function Row({ k, v, bold }) {
  return <div className={`flex justify-between ${bold ? "font-bold text-silver" : ""}`}><span>{k}</span><span>{v}</span></div>;
}

function ExportPane({ project, data, totals }) {
  const [versions, setVersions] = useState([]);
  async function loadVersions(){ const {data} = await api.get(`/projects/${project.id}/estimate/versions`); setVersions(data); }
  async function saveVer() {
    const name = window.prompt("Version name:"); if(!name) return;
    await api.post(`/projects/${project.id}/estimate/version`, { name });
    loadVersions();
  }
  useEffect(() => { loadVersions(); /* eslint-disable-next-line */ }, [project.id]);
  async function pdf() {
    const { data: inv } = await api.post("/invoices", { client_name: project.client_name || "", project_id: project.id, project_name: project.name, description: `Quote — ${project.name}`, amount_ex: totals.total_including_profit||0 });
    const r = await api.get(`/invoices/${inv.id}/pdf`, { responseType: "blob" });
    window.open(URL.createObjectURL(r.data), "_blank");
  }
  return (
    <div className="card space-y-3" data-testid="quote-builder">
      <h3 className="text-silver mb-2">Quote Builder — Preview</h3>
      <div className="text-sm">
        <div className="font-bold">{project.name} — {project.client_name}</div>
        <div className="opacity-70">{project.address}</div>
        <table className="table mt-3"><tbody>
          <tr><td>Builder's Work</td><td className="text-right">{fmtNZD((totals.labour_dollars||0)+(totals.materials_total||0)+(totals.misc_total||0))}</td></tr>
          <tr><td>Subcontractors (selected)</td><td className="text-right">{fmtNZD(totals.subcontractors_total)}</td></tr>
          <tr><td>Allowances & Preliminaries</td><td className="text-right">{fmtNZD((totals.sums_total||0)+(totals.overhead_total||0))}</td></tr>
          <tr><td className="font-bold">Subtotal (ex GST)</td><td className="text-right font-bold">{fmtNZD(totals.total_including_profit)}</td></tr>
          <tr><td>GST (15%)</td><td className="text-right">{fmtNZD(totals.gst)}</td></tr>
          <tr><td className="font-bold text-silver">TOTAL INCLUDING GST</td><td className="text-right font-bold text-silver">{fmtNZD(totals.total_including_gst)}</td></tr>
        </tbody></table>
      </div>
      <div className="flex gap-2">
        <button className="btn-silver" onClick={pdf} data-testid="btn-quote-pdf">Download quote PDF</button>
        <button className="btn-ghost" onClick={saveVer} data-testid="btn-save-named-version">Save named version</button>
      </div>
      {versions.length > 0 && (
        <div><h4 className="text-silver text-sm">Versions</h4>
          <ul className="text-xs">{versions.map(v=>(<li key={v.id}>• {v.name} — {(v.created_at||"").slice(0,16).replace("T"," ")}</li>))}</ul>
        </div>
      )}
    </div>
  );
}
